import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button, Card, Spin, Result, Typography, Space } from "antd";
import {
  LoadingOutlined,
  ExclamationCircleOutlined,
  ArrowLeftOutlined,
  CreditCardOutlined,
} from "@ant-design/icons";
import api from "api";

const { Title, Text } = Typography;

export default function Payments() {
  const [paymentContent, setPaymentContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  const PAYMENT_TIMEOUT_IN_SECOND = 500;
  const navigate = useNavigate();

  const { booking_code, booking_id, grand_total, caddy_id } =
    location.state || {};

  useEffect(() => {
    if (!booking_code || !booking_id || !grand_total) {
      setError("ข้อมูลการจองไม่ครบถ้วน");
      setLoading(false);
      return;
    }
    createPayment();
  }, [booking_code, booking_id, grand_total]);

  const createPayment = async () => {
    try {
      setError(null);
      const { data } = await api.paymentApi.createPayment({
        booking_code,
        booking_id,
        grand_total: Number((grand_total ?? 0).toFixed(2)),
      });
      const info = data.data;
      const payment_code = data.payment_code;

      if (payment_code === 0) {
        const url = info.pay_content;
        setTimeout(() => {
          window.location.href = url;
        }, PAYMENT_TIMEOUT_IN_SECOND);
        setPaymentContent(url);
      } else {
        setError("ไม่สามารถสร้างลิงก์ชำระเงินได้");
        setPaymentContent(null);
      }
    } catch (error) {
      setError(error.response?.data?.message || "เกิดข้อผิดพลาดในการเชื่อมต่อ");
      setPaymentContent(null);
      console.error("API Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    if (caddy_id) {
      navigate(`/book_caddy/${caddy_id}`, { replace: true });
    } else {
      navigate(-1);
    }
  };

  const handleRetry = () => {
    setLoading(true);
    setError(null);
    createPayment();
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
          padding: "20px",
        }}
      >
        <Card
          style={{
            maxWidth: 420,
            width: "100%",
            textAlign: "center",
            borderRadius: 16,
            boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
            border: "none",
          }}
          bodyStyle={{ padding: "40px 30px" }}
        >
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <div>
              <CreditCardOutlined
                style={{
                  fontSize: 60,
                  color: "#90C7C5",
                  marginBottom: 16,
                }}
              />
              <Spin
                indicator={
                  <LoadingOutlined
                    style={{
                      fontSize: 24,
                      color: "#90C7C5",
                      position: "absolute",
                      marginTop: -42,
                      marginLeft: 8,
                    }}
                    spin
                  />
                }
              />
            </div>

            <div>
              <Title level={3} style={{ color: "#2c3e50", marginBottom: 8 }}>
                กำลังเตรียมหน้าชำระเงิน
              </Title>
              <Text type="secondary" style={{ fontSize: 16 }}>
                รหัสการจอง:{" "}
                <Text code style={{ fontSize: 16, fontWeight: "bold" }}>
                  {booking_code}
                </Text>
              </Text>
              <br />
              <Text type="secondary" style={{ fontSize: 14, marginTop: 8 }}>
                กรุณารอสักครู่...
              </Text>
            </div>
          </Space>
        </Card>
      </div>
    );
  }

  // Success State (redirecting to payment)
  if (paymentContent) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          padding: "20px",
        }}
      >
        <Card
          style={{
            maxWidth: 450,
            width: "100%",
            textAlign: "center",
            borderRadius: 16,
            boxShadow: "0 15px 35px rgba(0,0,0,0.2)",
            border: "none",
          }}
          bodyStyle={{ padding: "40px 30px" }}
        >
          <Result
            icon={
              <div style={{ position: "relative" }}>
                <CreditCardOutlined
                  style={{
                    fontSize: 80,
                    color: "#52c41a",
                    marginBottom: 20,
                  }}
                />
                <Spin
                  indicator={
                    <LoadingOutlined
                      style={{
                        fontSize: 30,
                        color: "#52c41a",
                        position: "absolute",
                        top: 25,
                        left: "50%",
                        transform: "translateX(-50%)",
                      }}
                      spin
                    />
                  }
                />
              </div>
            }
            title={
              <Space direction="vertical" size="small">
                <Title level={2} style={{ color: "#52c41a", margin: 0 }}>
                  กำลังนำไปหน้าชำระเงิน
                </Title>
                <div
                  style={{
                    background: "#f6ffed",
                    padding: "12px 20px",
                    borderRadius: 8,
                    border: "1px solid #b7eb8f",
                  }}
                >
                  <Text style={{ fontSize: 18, color: "#389e0d" }}>
                    ยอดชำระ:{" "}
                    <Text strong style={{ fontSize: 24, color: "#52c41a" }}>
                      ฿{grand_total?.toLocaleString()}
                    </Text>
                  </Text>
                </div>
              </Space>
            }
            subTitle={
              <Text type="secondary" style={{ fontSize: 16 }}>
                หากไม่ได้รับการเปลี่ยนหน้าอัตโนมัติ กรุณากดปุ่มด้านล่าง
              </Text>
            }
            extra={
              <Button
                type="primary"
                size="large"
                onClick={() => (window.location.href = paymentContent)}
                style={{
                  height: 48,
                  fontSize: 16,
                  borderRadius: 8,
                  background: "#52c41a",
                  borderColor: "#52c41a",
                  boxShadow: "0 4px 15px rgba(82, 196, 26, 0.3)",
                }}
              >
                ไปหน้าชำระเงิน
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  // Error State
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)",
        padding: "20px",
      }}
    >
      <Card
        style={{
          maxWidth: 450,
          width: "100%",
          textAlign: "center",
          borderRadius: 16,
          boxShadow: "0 15px 35px rgba(0,0,0,0.2)",
          border: "none",
        }}
        bodyStyle={{ padding: "40px 30px" }}
      >
        <Result
          icon={
            <ExclamationCircleOutlined
              style={{
                color: "#ff4d4f",
                fontSize: 80,
                marginBottom: 20,
              }}
            />
          }
          status="error"
          title={
            <Title level={2} style={{ color: "#ff4d4f", margin: 0 }}>
              ไม่สามารถโหลดหน้าชำระเงินได้
            </Title>
          }
          subTitle={
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <div
                style={{
                  background: "#fff2f0",
                  padding: "16px",
                  borderRadius: 8,
                  border: "1px solid #ffccc7",
                }}
              >
                <Text style={{ fontSize: 16, color: "#a8071a" }}>{error}</Text>
              </div>
              {booking_code && (
                <Text type="secondary" style={{ fontSize: 14 }}>
                  รหัสการจอง:{" "}
                  <Text code style={{ fontSize: 14 }}>
                    {booking_code}
                  </Text>
                </Text>
              )}
            </Space>
          }
          extra={
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <Button
                type="primary"
                size="large"
                onClick={handleRetry}
                style={{
                  width: "100%",
                  height: 48,
                  fontSize: 16,
                  borderRadius: 8,
                  background: "#ff4d4f",
                  borderColor: "#ff4d4f",
                  boxShadow: "0 4px 15px rgba(255, 77, 79, 0.3)",
                }}
              >
                ลองใหม่อีกครั้ง
              </Button>
              <Button
                icon={<ArrowLeftOutlined />}
                size="large"
                onClick={handleGoBack}
                style={{
                  width: "100%",
                  height: 48,
                  fontSize: 16,
                  borderRadius: 8,
                }}
              >
                กลับไปหน้าจอง
              </Button>
            </Space>
          }
        />
      </Card>
    </div>
  );
}
